import {
  LogInPaths,
  REMOVED_SELF_SERVE_PATHS,
} from '@bbkAdminComponents/log-in-flow/log-in-paths';
import {
  type ActiveApp,
  type ActiveCompany,
  type ActiveUser,
} from '@bbkAdminRedux/app/reducers';
import { type LastActiveState } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { matchPath } from '@bbkShared/react-router';

const findCompany = (user: ActiveUser, companyKey?: string) => {
  if (!companyKey) return undefined;
  return user.companies.find((c) => c.internal_name === companyKey);
};
const findApp = (company: ActiveCompany, appKey?: string) => {
  if (!appKey) return undefined;
  if (appKey === '_') {
    return company.applications[0];
  }
  return company.applications.find((a) => a.encoded_id === appKey);
};

export type ActiveContext = {
  company?: ActiveCompany;
  app?: ActiveApp;
  redirect?: string;
};

const isPathOrDescendant = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

const isUnscopedPath = (pathname: string) =>
  [
    '/internal',
    LogInPaths.selector,
    LogInPaths.invite,
    LogInPaths.logout,
    LogInPaths.cbidpExchange,
    ...REMOVED_SELF_SERVE_PATHS,
  ].some((path) => isPathOrDescendant(pathname, path));

const isCompanyLandingPath = (pathname: string) =>
  pathname === LogInPaths.company ||
  pathname === `${LogInPaths.company}/` ||
  pathname === LogInPaths.signIn ||
  pathname === `${LogInPaths.signIn}/`;

const resolveDefaultApp = (
  company: ActiveCompany,
  remembered?: LastActiveState
) =>
  remembered?.companyKey === company.internal_name
    ? (findApp(company, remembered.appKey) ?? company.applications[0])
    : company.applications[0];

export const resolveActiveContext = (
  user: ActiveUser,
  pathname: string,
  lastActive?: LastActiveState
): ActiveContext => {
  if (isUnscopedPath(pathname) || !pathname.startsWith('/company')) {
    return {};
  }

  const remembered = lastActive?.email === user.email ? lastActive : undefined;
  const appMatch = matchPath<{ companyKey: string; appKey: string }>(pathname, {
    path: '/company/:companyKey/app/:appKey',
  });
  if (appMatch) {
    const company = findCompany(user, appMatch.params.companyKey);
    if (!company) return { redirect: LogInPaths.company };

    const app = findApp(company, appMatch.params.appKey);
    if (app) return { company, app };

    return {
      company,
      app: resolveDefaultApp(company, remembered),
      redirect: `${LogInPaths.company}/${company.internal_name}`,
    };
  }

  const companyMatch = matchPath<{ companyKey: string }>(pathname, {
    path: '/company/:companyKey',
  });
  if (companyMatch && !isCompanyLandingPath(pathname)) {
    const company = findCompany(user, companyMatch.params.companyKey);
    return company
      ? { company, app: resolveDefaultApp(company, remembered) }
      : { redirect: LogInPaths.company };
  }

  if (!isCompanyLandingPath(pathname)) return {};

  const company =
    findCompany(user, user.selected_company) ??
    findCompany(user, remembered?.companyKey) ??
    (user.companies.length === 1 ? user.companies[0] : undefined);
  if (!company) return {};

  return {
    company,
    app: resolveDefaultApp(company, remembered),
  };
};
